const PATH_DATA = / d="([^"]*)"/g;
const NUMBER = '(-?(?:\\d+\\.?\\d*|\\.\\d+)(?:e[-+]?\\d+)?)';
const SEPARATOR = '[\\s,]+';
// `M start A r r 0 1 sweep end L centre`: how Highcharts draws a pie slice that covers the whole circle. A tiny slice has
// the same end points but a small arc (flag 0): it stays empty.
const SLICE_ARC = new RegExp(
  `M\\s*${NUMBER}${SEPARATOR}${NUMBER}\\s*A\\s*${NUMBER}${SEPARATOR}${NUMBER}${SEPARATOR}0${SEPARATOR}1${SEPARATOR}([01])${SEPARATOR}` +
    `${NUMBER}${SEPARATOR}${NUMBER}\\s*L\\s*${NUMBER}${SEPARATOR}${NUMBER}`,
  'gi',
);
/** A slice whose arc starts and ends this close (in px) is a full circle: the end points are not a usable chord. */
const CLOSED_ARC_MAX_CHORD = 2;
const DECIMALS = 1e6;

const fixed = (value: number) => String(Math.round(value * DECIMALS) / DECIMALS);

/**
 * A pie slice of (nearly) 100% is drawn as one arc whose end meets its start. Once the coordinates are rounded the two
 * points are the same, and the PDF renderer, which then has no chord to place the circle on, draws a distorted shape.
 * The arc is split in two half circles, whose end points are a whole diameter apart.
 */
export const splitClosedArcs = (svg: string): string =>
  svg.replace(PATH_DATA, (attribute: string, d: string) => {
    const split = d.replace(SLICE_ARC, (match: string, sx, sy, rx, ry, sweep, ex, ey, cx, cy) => {
      const [startX, startY, radiusX, radiusY, endX, endY, centreX, centreY] = [sx, sy, rx, ry, ex, ey, cx, cy].map(Number);
      const isCircle = radiusX > 0 && radiusX === radiusY;
      const isClosed = Math.hypot(endX - startX, endY - startY) <= CLOSED_ARC_MAX_CHORD;
      // The line that follows must go to the centre: a ring slice goes to its inner arc and is left as it is.
      const toCentre = Math.abs(Math.hypot(centreX - startX, centreY - startY) - radiusX) <= 0.01 * radiusX + 0.01;
      if (!isCircle || !isClosed || !toCentre) return match;
      const oppositeX = 2 * centreX - startX;
      const oppositeY = 2 * centreY - startY;
      const half = `A ${rx} ${ry} 0 0 ${sweep}`;
      return `M ${sx} ${sy} ${half} ${fixed(oppositeX)} ${fixed(oppositeY)} ${half} ${sx} ${sy} L ${cx} ${cy}`;
    });
    return split === d ? attribute : ` d="${split}"`;
  });
